import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useRanking, type RankingState } from "../hooks/useRanking";
import type { Ranking } from "@pick-three-ai/types";

interface RankingContextValue {
  rankings: RankingState;
  setRankings: (rankings: RankingState) => void;
  setModel: (position: 1 | 2 | 3, model: string | null) => void;
  setBlurb: (position: 1 | 2 | 3, blurb: string) => void;
  title: string;
  setTitle: (title: string) => void;
  handle: string;
  setHandle: (handle: string) => void;
  clearRankings: () => void;
  isComplete: boolean;
  hasAnySelection: boolean;
  /** The original ranking being remixed (if any) */
  remixingFrom: Ranking | null;
  /** Set the ranking being remixed */
  setRemixingFrom: (ranking: Ranking | null) => void;
  /** Clear the remix source */
  clearRemixingFrom: () => void;
  /** The URL of the created ranking (persists across navigation) */
  createdUrl: string | null;
  /** Set the created URL along with the ranking state it was created for */
  setCreatedUrl: (url: string | null, forRankings?: RankingState, forTitle?: string, forHandle?: string) => void;
  /** The ranking state the URL was created for (used to detect changes) */
  createdUrlForState: { rankings: RankingState; title: string; handle: string } | null;
}

const RankingContext = createContext<RankingContextValue | null>(null);

export function RankingProvider({ children }: { children: ReactNode }) {
  const rankingState = useRanking();
  const [remixingFrom, setRemixingFromState] = useState<Ranking | null>(null);
  const [createdUrl, setCreatedUrlState] = useState<string | null>(null);
  const [createdUrlForState, setCreatedUrlForState] = useState<{
    rankings: RankingState;
    title: string;
    handle: string;
  } | null>(null);
  
  const setRemixingFrom = useCallback((ranking: Ranking | null) => {
    setRemixingFromState(ranking);
  }, []);
  
  const clearRemixingFrom = useCallback(() => {
    setRemixingFromState(null);
  }, []);
  
  const setCreatedUrl = useCallback((url: string | null, forRankings?: RankingState, forTitle?: string, forHandle?: string) => {
    setCreatedUrlState(url);
    if (url && forRankings) {
      // Deep copy the rankings to capture the state at creation time
      setCreatedUrlForState({
        rankings: forRankings.map(r => ({ ...r })) as RankingState,
        title: forTitle ?? "",
        handle: forHandle ?? "",
      });
    } else if (!url) {
      setCreatedUrlForState(null);
    }
  }, []);
  
  // Wrap clearRankings to also clear remix state and created URL
  const clearRankings = useCallback(() => {
    rankingState.clearRankings();
    setRemixingFromState(null);
    setCreatedUrlState(null);
    setCreatedUrlForState(null);
  }, [rankingState]);
  
  const contextValue: RankingContextValue = {
    ...rankingState,
    clearRankings, // Override with our wrapped version
    remixingFrom,
    setRemixingFrom,
    clearRemixingFrom,
    createdUrl,
    setCreatedUrl,
    createdUrlForState,
  };
  
  return (
    <RankingContext.Provider value={contextValue}>
      {children}
    </RankingContext.Provider>
  );
}

export function useRankingContext() {
  const context = useContext(RankingContext);
  if (!context) {
    throw new Error("useRankingContext must be used within a RankingProvider");
  }
  return context;
}


